/* @license
 * Copyright 2026 k1pp0
 * SPDX-License-Identifier: MIT
 */
import { WebXRCapture } from './three-components/WebXRCapture.js';
/**
 * Owns the `WebXRCapture` lifecycle for a single `<model-viewer-webxr-capture>`
 * instance.
 *
 *  - Constructs `WebXRCapture` when `onSessionStart` is invoked with a session
 *    that has `'camera-access'` enabled.
 *  - Drives `WebXRCapture.processFrame` from the host-bridge frame dispatcher.
 *  - Disposes `WebXRCapture` on session end.
 *
 * The global `'camera-access'` injection that used to live in
 * `beforeRequestSession` is now installed once at plugin connect time by
 * `host-bridge.ts` — see `ensureGlobalPatches`.
 */
export class WebXRCaptureProvider {
    constructor() {
        this.capture = null;
        this._supported = false;
    }
    /**
     * True once an AR session has started with `camera-access` enabled. False
     * before session start, after session end, or if the device declines the
     * feature.
     */
    get supported() {
        return this._supported;
    }
    onSessionStart(threeRenderer, session) {
        const sessionAny = session;
        this._supported = sessionAny.enabledFeatures != null &&
            sessionAny.enabledFeatures.indexOf('camera-access') !== -1;
        if (this._supported) {
            this.capture = new WebXRCapture(threeRenderer);
        }
    }
    onFrame(view, scene, viewCamera) {
        if (this.capture == null) {
            return;
        }
        this.capture.processFrame(view, scene, viewCamera);
    }
    onSessionEnd() {
        if (this.capture != null) {
            this.capture.dispose();
            this.capture = null;
        }
        this._supported = false;
    }
    /**
     * Schedules a capture of the current AR view. Resolves with `null` when
     * capture is unavailable (no active session, or `camera-access` not
     * granted).
     */
    requestCapture(options) {
        if (this.capture == null) {
            return Promise.resolve(null);
        }
        return this.capture.requestCapture(options);
    }
}
//# sourceMappingURL=webxr-capture-provider.js.map